// "Visual HD": a cena é desenhada no tamanho original (15x11 quadrados de 32 px) e ampliada por um
// filtro de pixel art (no estilo xBR) que arredonda as diagonais e suaviza as bordas, sem borrar
// o desenho como uma ampliação comum. Mantém o estilo do Tibia, só que com bordas limpas.
import { Geometry, Mesh, RenderTexture, Shader } from 'pixi.js';

const vertex = `
in vec2 aPosition;
in vec2 aUV;
out vec2 vUV;
uniform mat3 uProjectionMatrix;
uniform mat3 uWorldTransformMatrix;
uniform mat3 uTransformMatrix;
void main() {
  mat3 mvp = uProjectionMatrix * uWorldTransformMatrix * uTransformMatrix;
  gl_Position = vec4((mvp * vec3(aPosition, 1.0)).xy, 0.0, 1.0);
  vUV = aUV;
}`;

const fragment = `
in vec2 vUV;
out vec4 finalColor;
uniform sampler2D uTexture;
uniform vec2 uSize;
uniform float uScale;

vec4 px(vec2 c) { return texture(uTexture, c / uSize); }
float diff(vec4 a, vec4 b) {
  vec3 d = a.rgb - b.rgb;
  // diferença perceptual aproximada (YUV)
  float y = dot(d, vec3(0.299, 0.587, 0.114));
  float u = dot(d, vec3(-0.169, -0.331, 0.5));
  float v = dot(d, vec3(0.5, -0.419, -0.081));
  return 48.0 * abs(y) + 7.0 * abs(u) + 6.0 * abs(v) + 4.0 * abs(a.a - b.a);
}
bool eq(vec4 a, vec4 b) { return diff(a, b) < 0.75; }

void main() {
  vec2 p = vUV * uSize;
  vec2 tc = floor(p) + 0.5;
  vec2 f = fract(p);
  vec4 E = px(tc);
  vec2 q = step(0.5, f);
  vec2 dir = q * 2.0 - 1.0;                 // para qual canto do texel estamos olhando
  vec4 X  = px(tc + vec2(dir.x, 0.0));       // vizinho na horizontal
  vec4 Y  = px(tc + vec2(0.0, dir.y));       // vizinho na vertical
  vec4 Z  = px(tc + dir);                    // vizinho na diagonal
  vec4 X2 = px(tc - vec2(dir.x, 0.0));
  vec4 Y2 = px(tc - vec2(0.0, dir.y));
  vec2 c = abs(f - 0.5) * 2.0;               // 0 no centro, 1 no canto
  vec4 col = E;
  // canto arredondado: os dois vizinhos do canto são iguais entre si e diferentes do texel
  bool corner = eq(X, Y) && !eq(E, X) && (!eq(X, X2) || !eq(Y, Y2)) && (eq(E, X2) || eq(E, Y2) || !eq(E, Z));
  if (corner) {
    float aa = 1.2 / max(uScale, 1.0);
    float t = smoothstep(1.0 - aa, 1.0 + aa, c.x + c.y);
    col = mix(E, mix(X, Y, 0.5), t);
  }
  finalColor = col;
}`;

export class HdView {
  readonly texture: RenderTexture;
  readonly mesh: Mesh;
  private shader: Shader;
  constructor(w: number, h: number) {
    this.texture = RenderTexture.create({ width: w, height: h, resolution: 1, scaleMode: 'nearest' });
    const geometry = new Geometry({
      attributes: {
        aPosition: [0, 0, w, 0, w, h, 0, h],
        aUV: [0, 0, 1, 0, 1, 1, 0, 1],
      },
      indexBuffer: [0, 1, 2, 0, 2, 3],
    });
    this.shader = Shader.from({
      gl: { vertex, fragment },
      resources: {
        uTexture: this.texture.source,
        hd: { uSize: { value: [w, h], type: 'vec2<f32>' }, uScale: { value: 2, type: 'f32' } },
      },
    });
    this.mesh = new Mesh({ geometry, shader: this.shader });
  }
  setScale(s: number) { this.shader.resources.hd.uniforms.uScale = s; }
}
